/**
 * Normalise the wildly inconsistent country strings we get from Sentinel /
 * SigninLogs / GeoIP into the canonical names used by the world-atlas
 * TopoJSON (e.g. "United States of America", "Russia", "South Korea").
 *
 * Inputs we have to handle:
 *   - 2-letter ISO codes from `parse_json(LocationDetails).countryOrRegion`
 *     (Daily report's foreign-access rows): "MY", "US", "CN", …
 *   - 3-letter ISO codes that occasionally show up: "USA", "GBR", …
 *   - Full names from `geo_info_from_ip_address(IPAddress).country` and
 *     Sentinel's `Location` field: "United States", "China", "Korea, Republic of"
 *   - Common variants & alternate spellings.
 *
 * Anything we can't recognise is returned untouched so the caller can still
 * surface it (e.g. on a "Top countries" list) even if the map can't paint it.
 */

// ── ISO 3166-1 alpha-2 → atlas name ─────────────────────────────────────────
// Limited to commonly-seen attack-source countries plus all G20 members.
// Extending this list is cheap and doesn't break anything.
const ISO2_TO_NAME: Readonly<Record<string, string>> = {
  AE: 'United Arab Emirates',
  AF: 'Afghanistan',
  AR: 'Argentina',
  AT: 'Austria',
  AU: 'Australia',
  BD: 'Bangladesh',
  BE: 'Belgium',
  BG: 'Bulgaria',
  BR: 'Brazil',
  BY: 'Belarus',
  CA: 'Canada',
  CH: 'Switzerland',
  CL: 'Chile',
  CN: 'China',
  CO: 'Colombia',
  CZ: 'Czechia',
  DE: 'Germany',
  DK: 'Denmark',
  EG: 'Egypt',
  ES: 'Spain',
  FI: 'Finland',
  FR: 'France',
  GB: 'United Kingdom',
  GR: 'Greece',
  HK: 'China', // 110m world-atlas has no separate Hong Kong geometry; fold into China.
  HU: 'Hungary',
  ID: 'Indonesia',
  IE: 'Ireland',
  IL: 'Israel',
  IN: 'India',
  IQ: 'Iraq',
  IR: 'Iran',
  IT: 'Italy',
  JP: 'Japan',
  KH: 'Cambodia',
  KP: 'North Korea',
  KR: 'South Korea',
  KZ: 'Kazakhstan',
  LK: 'Sri Lanka',
  LV: 'Latvia',
  MA: 'Morocco',
  MM: 'Myanmar',
  MX: 'Mexico',
  MY: 'Malaysia',
  NG: 'Nigeria',
  NL: 'Netherlands',
  NO: 'Norway',
  NP: 'Nepal',
  NZ: 'New Zealand',
  PA: 'Panama',
  PE: 'Peru',
  PH: 'Philippines',
  PK: 'Pakistan',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  RS: 'Serbia',
  RU: 'Russia',
  SA: 'Saudi Arabia',
  SE: 'Sweden',
  SG: 'Singapore',
  SI: 'Slovenia',
  SK: 'Slovakia',
  SY: 'Syria',
  TH: 'Thailand',
  TR: 'Turkey',
  TW: 'Taiwan',
  UA: 'Ukraine',
  US: 'United States of America',
  UZ: 'Uzbekistan',
  VE: 'Venezuela',
  VN: 'Vietnam',
  ZA: 'South Africa',
};

// ── ISO 3166-1 alpha-3 → atlas name (subset) ────────────────────────────────
const ISO3_TO_NAME: Readonly<Record<string, string>> = {
  USA: 'United States of America',
  GBR: 'United Kingdom',
  RUS: 'Russia',
  CHN: 'China',
  KOR: 'South Korea',
  PRK: 'North Korea',
  CZE: 'Czechia',
  SRB: 'Serbia',
  TWN: 'Taiwan',
  HKG: 'Hong Kong S.A.R.',
  IRN: 'Iran',
  SYR: 'Syria',
  VNM: 'Vietnam',
  // Most ISO-3 codes won't appear in real KQL output, so keep this small.
};

// ── Free-text aliases → atlas name ──────────────────────────────────────────
// All keys are lower-cased for case-insensitive lookup.
const ALIAS_TO_NAME: Readonly<Record<string, string>> = {
  'united states': 'United States of America',
  'united states of america': 'United States of America',
  usa: 'United States of America',
  'u.s.': 'United States of America',
  'u.s.a.': 'United States of America',
  america: 'United States of America',
  'united kingdom': 'United Kingdom',
  uk: 'United Kingdom',
  'great britain': 'United Kingdom',
  britain: 'United Kingdom',
  england: 'United Kingdom',
  scotland: 'United Kingdom',
  wales: 'United Kingdom',
  'russian federation': 'Russia',
  russia: 'Russia',
  'south korea': 'South Korea',
  'korea, republic of': 'South Korea',
  'republic of korea': 'South Korea',
  'korea republic': 'South Korea',
  'north korea': 'North Korea',
  'democratic people\'s republic of korea': 'North Korea',
  "korea, democratic people's republic of": 'North Korea',
  'czech republic': 'Czechia',
  'czech rep.': 'Czechia',
  czechia: 'Czechia',
  serbia: 'Serbia',
  'republic of serbia': 'Serbia',
  vietnam: 'Vietnam',
  'viet nam': 'Vietnam',
  'iran, islamic republic of': 'Iran',
  iran: 'Iran',
  'syrian arab republic': 'Syria',
  syria: 'Syria',
  taiwan: 'Taiwan',
  'taiwan, province of china': 'Taiwan',
  // 110m world-atlas has no separate Hong Kong geometry; fold into China.
  'hong kong': 'China',
  'hong kong sar': 'China',
  'hong kong s.a.r.': 'China',
  'hong kong s.a.r': 'China',
  // Things the atlas doesn't actually carve out get nearest-equivalent
  // mappings or pass-through (handled by the default branch in normaliseCountry).
};

/**
 * Map any reasonable country string to the canonical name used by the
 * world-atlas TopoJSON we ship with. Unknown inputs are returned trimmed but
 * otherwise unchanged so the caller can still display them in the side list.
 */
export function normaliseCountry(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';

  // 2-letter ISO code (very common in SigninLogs).
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    const m = ISO2_TO_NAME[trimmed.toUpperCase()];
    if (m) return m;
  }
  // 3-letter ISO code.
  if (/^[A-Za-z]{3}$/.test(trimmed)) {
    const m = ISO3_TO_NAME[trimmed.toUpperCase()];
    if (m) return m;
  }
  const m = ALIAS_TO_NAME[trimmed.toLowerCase()];
  if (m) return m;
  return trimmed;
}

/**
 * Convenience: aggregate a list of `{country, count}` rows by canonical
 * country name. The input may have duplicates (e.g. "US" and "United States"
 * both appearing), which we collapse into one bucket.
 */
export function aggregateByCountry<T extends { country: string; count: number }>(
  rows: ReadonlyArray<T>,
): Array<{ country: string; count: number }> {
  const acc = new Map<string, number>();
  for (const row of rows) {
    const name = normaliseCountry(row.country);
    if (!name) continue;
    acc.set(name, (acc.get(name) ?? 0) + (Number.isFinite(row.count) ? row.count : 0));
  }
  return Array.from(acc, ([country, count]) => ({ country, count }))
    .filter(r => r.count > 0)
    .sort((a, b) => b.count - a.count);
}
